import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Pilates studio images
import pilates1 from '../../../../assets/img/gallery/pilates/1.jpeg';
import pilates2 from '../../../../assets/img/gallery/pilates/2.jpeg';
import pilates3 from '../../../../assets/img/gallery/pilates/3.jpeg';
import pilates4 from '../../../../assets/img/gallery/pilates/4.jpeg';
import pilates5 from '../../../../assets/img/gallery/pilates/5.jpeg';
import pilates6 from '../../../../assets/img/gallery/pilates/6.jpeg';
import pilates7 from '../../../../assets/img/gallery/pilates/7.jpeg';
import pilates8 from '../../../../assets/img/gallery/pilates/8.jpeg';
import pilates9 from '../../../../assets/img/gallery/pilates/9.jpeg';

// Clinic images (new)
import clinic9 from '../../../../assets/img/gallery/9.jpeg';
import clinic10 from '../../../../assets/img/gallery/10.jpeg';
import clinic11 from '../../../../assets/img/gallery/11.jpeg';
import clinic12 from '../../../../assets/img/gallery/12.jpeg';

import { Features } from '../../../../assets/features/Features';
import { useLanguage } from '../../../../context/LanguageContext';
import SEO from '../../seo/SEO';
import { seoData } from '../../../../data/seoData';

const allGalleryImages = [
  pilates1, clinic9, pilates2, clinic10,
  pilates3, clinic11, pilates4, clinic12,
  pilates5, pilates6, pilates7, pilates8, pilates9,
];

const GalleryPage: React.FC = () => {
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);

    AOS.init({
      duration: 1000,
      easing: 'ease-in-out',
      once: true,
      mirror: false
    });

    try {
      const GLightbox = require('glightbox');
      GLightbox({
        selector: '.gallery-lightbox'
      });
    } catch (error) {
      console.warn('GLightbox not available:', error);
    }
  }, []);

  return (
    <>
      <SEO
        title={seoData.gallery.title}
        description={seoData.gallery.description}
        keywords={seoData.gallery.keywords}
        canonical={seoData.gallery.canonical}
      />
      {/* Hero Section */}
      <section
        className="gallery-hero d-flex align-items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${pilates1})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '40vh',
          position: 'relative'
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-12 text-center text-white">
              <h1
                className="display-4 fw-bold"
                data-aos="fade-up"
                style={{
                  color: '#ffffff',
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)'
                }}
              >
                {Features.galleryPage.hero.title[language]}
              </h1>
              <div className="divider mx-auto my-4" data-aos="fade-up" data-aos-delay="100" style={{
                width: '80px',
                height: '4px',
                background: 'var(--primary-color, #1977cc)',
                margin: '0 auto'
              }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Title Section */}
      <section className="gallery-title section-bg py-5">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center" data-aos="fade-up">
              <h2 className="fw-bold">{Features.galleryPage.title.main[language]}</h2>
              <p className="lead mt-3">{Features.galleryPage.title.subtitle[language]}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="gallery-content section py-5">
        <div className="container">
          <div className="row g-4">
            {allGalleryImages.map((img, index) => (
              <div className="col-lg-4 col-md-6" data-aos="zoom-in" data-aos-delay={((index % 3) + 1) * 100} key={index}>
                <div className="gallery-page-item">
                  <a href={img} className="gallery-lightbox glightbox" data-gallery="gallery-full">
                    <div className="gallery-page-img-wrapper">
                      <img src={img} alt={Features.galleryPage.imageAlt[language]} />
                    </div>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="newsletter-section section-bg py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="newsletter-box bg-white p-4 rounded shadow" data-aos="fade-up">
                <h4 className="text-center mb-4">{Features.galleryPage.newsletter.title[language]}</h4>
                <p className="text-center mb-4">{Features.galleryPage.newsletter.subtitle[language]}</p>

                <form className="row g-3">
                  <div className="col-12">
                    <input type="email" className="form-control" placeholder={Features.galleryPage.newsletter.emailPlaceholder[language]} required />
                  </div>
                  <div className="col-12">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="acceptTerms" required />
                      <label className="form-check-label" htmlFor="acceptTerms">
                        {Features.galleryPage.newsletter.termsCheckbox[language]}
                      </label>
                    </div>
                  </div>
                  <div className="col-12 text-center">
                    <button type="submit" className="btn btn-primary rounded-pill px-4 py-2">
                      {Features.galleryPage.newsletter.subscribeButton[language]}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .gallery-page-item {
          position: relative;
          overflow: hidden;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .gallery-page-img-wrapper {
          width: 100%;
          padding-top: 75%; /* 4:3 aspect ratio */
          position: relative;
          overflow: hidden;
        }

        .gallery-page-img-wrapper img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .gallery-page-item:hover .gallery-page-img-wrapper img {
          transform: scale(1.05);
        }

        .gallery-page-item a {
          display: block;
          cursor: pointer;
        }
      `}</style>
    </>
  );
};

export default GalleryPage;
